import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { GlassCard } from '../../components/GlassCard';
import { PageHeader } from '../../components/AppLayout';
import { useUserStore } from '../../store/userStore';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import { STAGE_LABEL } from '../../data/constants';
import { getTestimonials, uploadAvatar, updateUser, fileUrl, deleteAccount } from '../../lib/api';
import { ConsentSettings } from '../../components/ConsentSettings';
import { WellbeingStatementCard } from '../../components/WellbeingStatementCard';

const STAGES = [
  { key: 'pre', label: 'Pre-menopause' },
  { key: 'peri', label: 'Perimenopause' },
  { key: 'post', label: 'Post-menopause' },
];

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout, isPremiumUser, checkPremiumStatus, setUser } = useUserStore();
  const [testimonials, setTestimonials] = useState([]);
  const { canInstall, promptInstall, isIOS, isStandalone } = useInstallPrompt();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await deleteAccount(user.id);
      toast.success('Your account and data have been deleted');
      logout();
      navigate('/login');
    } catch (e) {
      toast.error('Could not delete your account. Please try again.');
      setDeleting(false);
    }
  };

  useEffect(() => {
    setForm({
      name: user?.name || '',
      age: user?.age || '',
      menopause_stage: user?.menopause_stage || 'peri',
      phone: user?.phone || '',
      date_of_birth: user?.date_of_birth || '',
      address: user?.address || '',
      city: user?.city || '',
      postcode: user?.postcode || '',
      country: user?.country || '',
    });
  }, [user]);

  const installApp = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') toast.success('Adding Auralis to your home screen');
    else if (outcome !== 'accepted' && !canInstall) {
      toast.message(isIOS ? 'Tap the Share icon, then "Add to Home Screen"' : 'Use your browser menu → "Install app" / "Add to Home Screen"');
    }
  };

  useEffect(() => {
    checkPremiumStatus();
    getTestimonials().then((t) => setTestimonials((t || []).slice(0, 3))).catch(() => {});
  }, []); // eslint-disable-line

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAvatar(user.id, file);
      setUser({ ...user, profile_image: res.profile_image });
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not upload image');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const saveDetails = async () => {
    setSaving(true);
    try {
      const updates = { ...form, age: form.age ? parseInt(form.age, 10) : user.age };
      const updated = await updateUser(user.id, updates);
      setUser(updated);
      toast.success('Details saved');
      setEditing(false);
    } catch (err) {
      toast.error('Could not save your details');
    } finally {
      setSaving(false);
    }
  };

  const doLogout = () => {
    logout();
    navigate('/login');
  };

  const avatar = fileUrl(user?.profile_image);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 pt-6 sm:pt-10">
      <PageHeader title="Profile" subtitle="Your account and membership." />

      <GlassCard className="p-6 mb-5 flex items-center gap-4 animate-fade-up">
        <div className="relative shrink-0">
          {avatar ? (
            <img src={avatar} alt="Profile" className="w-20 h-20 rounded-2xl object-cover" data-testid="profile-avatar-img" />
          ) : (
            <div className="grid place-items-center w-20 h-20 rounded-2xl text-2xl font-semibold text-white" style={{ background: 'linear-gradient(135deg,#A855F7,#7C3AED)' }}>
              {(user?.name || 'U').charAt(0)}
            </div>
          )}
          <button
            data-testid="avatar-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="absolute -bottom-1.5 -right-1.5 grid place-items-center w-8 h-8 rounded-full text-white shadow-lg aura-btn-gradient"
            title="Change photo"
          >
            {uploading ? <i className="fa-solid fa-circle-notch fa-spin text-xs" /> : <i className="fa-solid fa-camera text-xs" />}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onPickAvatar} className="hidden" data-testid="avatar-file-input" />
        </div>
        <div className="flex-1">
          <div className="font-display text-2xl">{user?.name}</div>
          <div className="text-[var(--aura-ink-soft)] text-sm">{user?.email}</div>
          <div className="flex gap-2 mt-2 flex-wrap">
            <span className="text-xs rounded-full px-3 py-1 bg-[var(--aura-purple)]/10 text-[var(--aura-purple)]">{STAGE_LABEL[user?.menopause_stage] || 'Perimenopause'}</span>
            {user?.age ? <span className="text-xs rounded-full px-3 py-1 bg-[var(--aura-teal)]/12 text-[var(--aura-teal)]">Age {user.age}</span> : null}
          </div>
        </div>
      </GlassCard>

      <WellbeingStatementCard />

      {/* Personal details */}
      <GlassCard className="p-6 mb-5 animate-fade-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg">Personal details</h3>
          {!editing ? (
            <button data-testid="edit-details-btn" onClick={() => setEditing(true)} className="text-sm font-semibold text-[var(--aura-purple)] flex items-center gap-2">
              <i className="fa-solid fa-pen" /> Edit
            </button>
          ) : (
            <button onClick={() => setEditing(false)} className="text-sm text-[var(--aura-ink-soft)]">Cancel</button>
          )}
        </div>

        {!editing ? (
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {[
              ['Full name', user?.name],
              ['Date of birth', user?.date_of_birth],
              ['Phone', user?.phone],
              ['Life stage', STAGE_LABEL[user?.menopause_stage]],
              ['Address', user?.address],
              ['City', user?.city],
              ['Postcode', user?.postcode],
              ['Country', user?.country],
            ].map(([label, val]) => (
              <div key={label} className="flex flex-col">
                <span className="text-xs uppercase tracking-wide text-[var(--aura-ink-soft)]">{label}</span>
                <span className="font-medium">{val || <span className="text-[var(--aura-ink-soft)] font-normal">Not set</span>}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Full name" testid="field-name" value={form.name} onChange={set('name')} />
            <Field label="Age" testid="field-age" type="number" value={form.age} onChange={set('age')} />
            <div>
              <label className="block text-xs uppercase tracking-wide text-[var(--aura-ink-soft)] mb-1">Life stage</label>
              <select data-testid="field-stage" value={form.menopause_stage} onChange={set('menopause_stage')} className="w-full rounded-xl border border-[var(--aura-line)] px-3 py-2.5 outline-none focus:border-[var(--aura-purple)] bg-white">
                {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <Field label="Date of birth" testid="field-dob" type="date" value={form.date_of_birth} onChange={set('date_of_birth')} />
            <Field label="Phone" testid="field-phone" value={form.phone} onChange={set('phone')} />
            <Field label="Address" testid="field-address" value={form.address} onChange={set('address')} />
            <Field label="City" testid="field-city" value={form.city} onChange={set('city')} />
            <Field label="Postcode" testid="field-postcode" value={form.postcode} onChange={set('postcode')} />
            <Field label="Country" testid="field-country" value={form.country} onChange={set('country')} />
            <div className="sm:col-span-2">
              <button data-testid="save-details-btn" onClick={saveDetails} disabled={saving} className="w-full aura-btn-gradient text-white font-semibold rounded-2xl py-3 flex items-center justify-center gap-2 disabled:opacity-70">
                {saving ? <i className="fa-solid fa-circle-notch fa-spin" /> : <><i className="fa-solid fa-floppy-disk" /> Save details</>}
              </button>
            </div>
          </div>
        )}
      </GlassCard>

      <ConsentSettings userId={user?.id} />

      <GlassCard className="p-6 mb-5 animate-fade-up" style={{ background: isPremiumUser ? undefined : 'linear-gradient(135deg, rgba(124,58,237,0.06), rgba(236,72,153,0.06))' }}>
        <div className="flex items-center gap-3 mb-2">
          <div className="grid place-items-center w-10 h-10 rounded-xl bg-[var(--aura-purple)]/12 text-[var(--aura-purple)]"><i className="fa-solid fa-crown" /></div>
          <div>
            <div className="font-display text-lg">{isPremiumUser ? 'Premium member' : 'Auralis Premium'}</div>
            <div className="text-xs text-[var(--aura-ink-soft)]">{isPremiumUser ? 'You have full access to precision insights.' : 'Unlock what’s really driving how you feel.'}</div>
          </div>
        </div>
        {!isPremiumUser && (
          <button data-testid="upgrade-btn" onClick={() => navigate('/upgrade')} className="mt-3 aura-btn-gradient text-white font-semibold rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2">
            <i className="fa-solid fa-unlock" /> Upgrade to Premium
          </button>
        )}
      </GlassCard>

      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        {[
          { label: 'My assessment', icon: 'fa-wand-magic-sparkles', to: '/assessment/results' },
          { label: 'Biomarkers', icon: 'fa-vial', to: '/biomarkers' },
          { label: 'Weight & BMI', icon: 'fa-weight-scale', to: '/weight' },
        ].map((m) => (
          <GlassCard key={m.label} hover onClick={() => navigate(m.to)} className="p-4 cursor-pointer flex items-center gap-3">
            <div className="grid place-items-center w-10 h-10 rounded-xl bg-[var(--aura-purple)]/10 text-[var(--aura-purple)]"><i className={`fa-solid ${m.icon}`} /></div>
            <span className="font-medium text-sm">{m.label}</span>
          </GlassCard>
        ))}
      </div>

      {testimonials.length > 0 && (
        <div className="mb-6">
          <h3 className="font-display text-xl mb-3">From our community</h3>
          <div className="flex flex-col gap-3">
            {testimonials.map((t, i) => (
              <GlassCard key={i} className="p-5">
                <div className="flex gap-1 mb-2 text-amber-400">{Array.from({ length: t.rating || 5 }).map((_, j) => <i key={j} className="fa-solid fa-star text-xs" />)}</div>
                <p className="text-[var(--aura-ink)] italic mb-2">“{t.content}”</p>
                <div className="text-sm font-semibold">{t.name} <span className="text-[var(--aura-ink-soft)] font-normal capitalize">· {t.role}</span></div>
              </GlassCard>
            ))}
          </div>
        </div>
      )}

      {!isStandalone && (
        <GlassCard className="p-5 mb-4 flex items-center gap-4 animate-fade-up" data-testid="install-card">
          <div className="grid place-items-center w-12 h-12 rounded-2xl text-white shrink-0" style={{ background: 'linear-gradient(135deg,#A855F7,#7C3AED)' }}>
            <i className="fa-solid fa-mobile-screen-button text-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg">Install the Auralis app</div>
            <div className="text-sm text-[var(--aura-ink-soft)]">Add Auralis to your phone&apos;s home screen for one-tap access and a full-screen experience.</div>
          </div>
          <button data-testid="profile-install-btn" onClick={installApp} className="aura-btn-gradient text-white font-semibold rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2 shrink-0">
            <i className="fa-solid fa-download" /> Install
          </button>
        </GlassCard>
      )}

      <button data-testid="profile-logout-btn" onClick={doLogout} className="w-full rounded-2xl py-3.5 border border-[var(--aura-line)] text-[var(--aura-ink-soft)] font-medium flex items-center justify-center gap-2 hover:text-[var(--aura-purple)] transition-base mb-4">
        <i className="fa-solid fa-arrow-right-from-bracket" /> Sign out
      </button>

      {/* Danger zone */}
      <GlassCard className="p-6 mb-8 animate-fade-up" style={{ borderColor: 'rgba(239,68,68,0.25)', background: 'rgba(239,68,68,0.03)' }} data-testid="danger-zone">
        <div className="flex items-center gap-2 mb-1">
          <div className="grid place-items-center w-9 h-9 rounded-xl bg-[#EF4444]/12 text-[#EF4444]"><i className="fa-solid fa-triangle-exclamation" /></div>
          <h3 className="font-display text-lg">Close account</h3>
        </div>
        <p className="text-sm text-[var(--aura-ink-soft)] mb-4">Permanently delete your Auralis account and erase all your personal data — check-ins, assessments, biomarkers, messages and more. This cannot be undone.</p>
        <button
          data-testid="open-delete-btn"
          onClick={() => { setShowDelete(true); setConfirmText(''); }}
          className="rounded-2xl px-5 py-3 font-semibold text-white flex items-center gap-2"
          style={{ background: '#EF4444' }}
        >
          <i className="fa-solid fa-trash-can" /> Close account & erase my data
        </button>
      </GlassCard>

      {showDelete && (
        <div className="fixed inset-0 z-[60] grid place-items-center px-4" style={{ background: 'rgba(11,11,31,0.55)', backdropFilter: 'blur(4px)' }} data-testid="delete-modal">
          <div className="bg-white rounded-3xl p-6 sm:p-7 w-[min(460px,94vw)] animate-fade-scale">
            <div className="grid place-items-center w-14 h-14 rounded-2xl mx-auto mb-4 bg-[#EF4444]/12 text-[#EF4444]"><i className="fa-solid fa-trash-can text-xl" /></div>
            <h2 className="font-display text-2xl text-center mb-2">Erase your account?</h2>
            <p className="text-sm text-[var(--aura-ink-soft)] text-center mb-4">This permanently deletes your profile and all health data. There's no way to recover it. To confirm, type <span className="font-semibold text-[var(--aura-ink)]">DELETE</span> below.</p>
            <input
              data-testid="delete-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE"
              className="w-full rounded-xl border border-[var(--aura-line)] px-4 py-3 outline-none focus:border-[#EF4444] text-center font-semibold tracking-wider mb-4"
            />
            <div className="flex gap-3">
              <button data-testid="cancel-delete-btn" onClick={() => setShowDelete(false)} className="flex-1 rounded-2xl py-3 border border-[var(--aura-line)] font-medium">Cancel</button>
              <button
                data-testid="confirm-delete-btn"
                onClick={doDelete}
                disabled={confirmText.trim().toUpperCase() !== 'DELETE' || deleting}
                className="flex-1 rounded-2xl py-3 font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: '#EF4444' }}
              >
                {deleting ? <i className="fa-solid fa-circle-notch fa-spin" /> : <><i className="fa-solid fa-trash-can" /> Delete forever</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wide text-[var(--aura-ink-soft)] mb-1">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value || ''}
        onChange={onChange}
        className="w-full rounded-xl border border-[var(--aura-line)] px-3 py-2.5 outline-none focus:border-[var(--aura-purple)]"
      />
    </div>
  );
}
